import { areaOf, isType, prop, type PropsElement } from '../props';
import { makeTools, MATERIAL_CLASSES, materialClassIn, typeIn, typeWords } from '../tools';
import type { TakeoffResult } from '../tools';
import { callTool, effectsFrom, highlightsFrom, suggestFrom, traceLine } from '../trace';
import type { Answer, AskContext, Engine, Suggest, ToolCall, ToolFn } from '../types';
import { auditText, buildText, checkText, exportText, queryText, sceneText } from './render';

// No model: a handful of intents (DE/EN) over the same tools. The offline floor.
export const keywordEngine: Engine = {
  name: 'keyword',
  async answer(question, ctx) {
    return route(question, ctx);
  },
};

// True when the rules would run a build recipe for this question: the build phrases and a
// plain yes to a pending suggest. A model engine hands these to the rules and keeps the free
// questions for itself.
export function matchesBuild(question: string, ctx: AskContext): boolean {
  const q = question.toLowerCase();
  if (ctx.history?.at(-1)?.suggest && AFFIRMATION.test(q.trim())) return true;
  if (SPREADSHEET.test(q) || (EXPORT_WORD.test(q) && QUANTITIES.test(q))) return false;
  return BUILD_RES.some((re) => re.test(q)) || CHANGE_RE.test(q);
}

// The changes the rules run, as the visitor can type them. Everything else that reads like a
// change request gets this list back instead of a guess.
export const BUILD_COMMANDS = [
  'Put another floor on top',
  'Add a flat roof',
  'Add a gable roof',
  'Add a stair',
  'Remove the top floor',
  'Undo',
  'Export the IFC',
];
const BUILD_SYNTAX = [
  'add a wall from 0,0 to 5,0',
  'cut a door into wall #1234',
  'set material Concrete on #1234',
  'remove #1234 #5678',
];

export function commandsText(de: boolean): string {
  const list = [...BUILD_COMMANDS.map((c) => c.toLowerCase()), ...BUILD_SYNTAX].join(' · ');
  return de
    ? `Änderungen laufen über feste Befehle: ${list}. Für freie Geometrie: die gleichen Werkzeuge in Claude Desktop über MCP.`
    : `Changes run through fixed commands: ${list}. For free geometry, plug the same tools into Claude Desktop over MCP.`;
}

export async function route(question: string, ctx: AskContext): Promise<Answer> {
  const q = question.toLowerCase();
  const tools = makeTools(ctx.elements, ctx.bounds, ctx.store);
  const calls: ToolCall[] = [];
  const done = (text: string, highlights?: number[]): Answer => ({
    text,
    highlights: highlights ?? highlightsFrom(calls),
    trace: calls.map(traceLine),
    calls,
    via: 'keyword',
    effects: effectsFrom(calls),
    suggest: suggestFrom(calls),
  });
  const de = /[äöüß]|wie viel|welche|gibt es|sind|pro geschoss|kubik|fläche|mengen|exportiere/.test(
    q,
  );

  // The "it needs stairs" loop: a finding's suggest carried on the last turn, taken up on
  // a plain yes. Checked before any filter parsing so a bare "yes" never falls through.
  const lastSuggest = ctx.history?.at(-1)?.suggest;
  if (lastSuggest && AFFIRMATION.test(q.trim()))
    return done(await runSuggests(tools, calls, lastSuggest, de));

  // ahead of the build intents: "exportiere die Mengen" is not an IFC export
  if (SPREADSHEET.test(q) || (EXPORT_WORD.test(q) && QUANTITIES.test(q))) {
    const groupBy = /by material|per material|nach material|je material/.test(q)
      ? 'material'
      : /by type|per type|nach typ|je typ/.test(q)
        ? 'type'
        : /by storey|per storey|by level|nach geschoss|pro geschoss|je geschoss/.test(q)
          ? 'storey'
          : null;
    const r = (await callTool(
      tools,
      'export_takeoff',
      { format: /\bcsv\b/.test(q) ? 'csv' : 'xlsx', ...(groupBy ? { groupBy } : {}) },
      calls,
    )) as Record<string, unknown>;
    return done(exportText(r, de));
  }

  if (CHANGED_RE.test(q)) {
    const r = (await callTool(tools, 'check_building', {}, calls)) as Record<string, unknown>;
    if (r.error)
      return done(
        de ? `Das ging nicht: ${String(r.error)}` : `That did not work: ${String(r.error)}`,
      );
    return done(checkText(r, de));
  }

  const build = await buildRoute(q, tools, calls, de, ctx);
  if (build) return done(build);
  if (CHANGE_RE.test(q)) return done(commandsText(de), []);

  const type = typeIn(q);
  const material = materialClassIn(q);
  const storey = storeyIn(q, ctx.elements);

  const filters = {
    ...(type ? { type } : {}),
    ...(material ? { material } : {}),
    ...(storey ? { storey } : {}),
  };
  const hasFilter = Boolean(type || material || storey);
  const withScene = async (action: string, extra: Record<string, unknown> = {}) => {
    const r = (await callTool(tools, 'scene', { action, ...filters, ...extra }, calls)) as Record<
      string,
      unknown
    >;
    return done(sceneText(r, de));
  };

  if (/\b(reset|zurücksetzen|zuruecksetzen|alles zeigen|show everything|clear)\b/.test(q))
    return withScene('reset');
  if (
    /\b(audit|prüfe|pruefe|check the model|data quality|datenqualität|datenqualitaet|probleme|issues)\b/.test(
      q,
    )
  ) {
    const r = (await callTool(tools, 'audit_model', {}, calls)) as Record<string, unknown>;
    return done(auditText(r, de));
  }
  if (
    /\b(colou?r by|colou?r the|einfärben|einfaerben|färbe|faerbe|nach material|by material|by type|by storey|nach typ|nach geschoss)\b/.test(
      q,
    )
  ) {
    const field = /volume|volumen/.test(q)
      ? 'volume'
      : /area|fläche|flaeche/.test(q)
        ? 'area'
        : /storey|geschoss|level/.test(q)
          ? 'storey'
          : /\btype\b|typ\b/.test(q)
            ? 'type'
            : 'material';
    return withScene('colour_by', { field });
  }
  if (
    /\b(section|schnitt|cut|schneide|look into|plan view|grundriss)\b/.test(q) &&
    (storey || /\d/.test(q))
  ) {
    const m = q.match(/(\d+(?:[.,]\d+)?)\s*m\b/);
    return withScene('section', storey ? { storey } : { at: Number(m?.[1]?.replace(',', '.')) });
  }
  if (hasFilter && /\b(label|beschrifte|beschriften|annotate|write the|schreib)\b/.test(q)) {
    const field = /volume|volumen|m³/.test(q)
      ? 'volume'
      : /area|fläche|flaeche|m²/.test(q)
        ? 'area'
        : /material/.test(q)
          ? 'material'
          : undefined;
    return withScene('label', field ? { field } : {});
  }
  if (hasFilter && /\b(only|isolate|nur|isolier|hide everything else|alles andere)\b/.test(q))
    return withScene('isolate');
  if (hasFilter && /\b(focus|fly to|zoom|go to|flieg|fokus|zeig mir wo|where (is|are))\b/.test(q))
    return withScene('focus');
  if (/\b(near|nearby|next to|around|in der nähe|neben|um)\b/.test(q)) {
    const m = q.match(/#?(\d{3,})/);
    if (m) {
      const r = (await callTool(
        tools,
        'elements_near',
        { id: Number(m[1]), ...(type ? { type } : {}) },
        calls,
      )) as Record<string, unknown>;
      return done(queryText(r, de));
    }
  }

  if (isType_(type, 'IfcSpace') && /larger|bigger|more than|over|>|größer|mehr als|über/.test(q)) {
    const m = q.match(/(\d+(?:[.,]\d+)?)/);
    const limit = m ? Number(m[1]!.replace(',', '.')) : 20;
    const rooms = ctx.elements
      .filter((e) => isType(e, 'IfcSpace'))
      .map((e) => ({ e, area: areaOf(e)?.value ?? 0 }))
      .filter((r) => r.area > limit)
      .sort((a, b) => b.area - a.area);
    calls.push({
      tool: 'find_elements',
      input: {
        type: 'IfcSpace',
        pset: 'PSet_Revit_Dimensions',
        prop: 'Area',
        op: 'gt',
        value: limit,
      },
      output: { count: rooms.length, ids: rooms.map((r) => r.e.expressID) },
    });
    const list = rooms
      .map((r) =>
        `${prop(r.e, 'PSet_Revit_Identity Data', 'Number') ?? ''} ${prop(r.e, 'PSet_Revit_Identity Data', 'Name') ?? r.e.name} (${r.area.toFixed(1)} m², ${r.e.storey})`.trim(),
      )
      .join('; ');
    return done(
      de
        ? `${rooms.length} Räume sind größer als ${limit} m²: ${list}.`
        : `${rooms.length} rooms are larger than ${limit} m²: ${list}.`,
    );
  }

  if (VOLUME.test(q) || AREA.test(q) || (material && HOW_MUCH.test(q))) {
    const quantity = AREA.test(q) && !VOLUME.test(q) ? 'area' : 'volume';
    const r = (await callTool(
      tools,
      'quantity_takeoff',
      {
        quantity,
        ...(type ? { type } : {}),
        ...(material ? { material } : {}),
        ...(storey ? { storey } : {}),
      },
      calls,
    )) as TakeoffResult;
    return done(takeoffText(r, de));
  }

  if (isType_(type, 'IfcDoor') && /fire|brand|rating/.test(q)) {
    const all = (await callTool(tools, 'count_elements', { type: 'IfcDoor' }, calls)) as {
      count: number;
    };
    const r = (await callTool(
      tools,
      'find_elements',
      { type: 'IfcDoor', pset: 'Pset_DoorCommon', prop: 'FireRating', op: 'missing' },
      calls,
    )) as { count: number; ids: number[] };
    return done(
      de
        ? `${r.count} von ${all.count} Türen haben keine Brandschutzklasse; der Revit-Export hat Pset_DoorCommon.FireRating als Platzhalter gelassen. Sie sind markiert.`
        : `${r.count} of ${all.count} doors carry no fire rating; the Revit export left Pset_DoorCommon.FireRating as a placeholder. They are highlighted.`,
      r.ids,
    );
  }

  if (isType_(type, 'IfcWall') && /load|bearing|tragend/.test(q)) {
    const r = (await callTool(
      tools,
      'find_elements',
      { type: 'IfcWall', pset: 'Pset_WallCommon', prop: 'LoadBearing', op: 'eq', value: 'true' },
      calls,
    )) as { count: number; ids: number[] };
    if (r.count === 0)
      return done(
        de
          ? 'Keine Wand in diesem Modell ist in Pset_WallCommon als tragend markiert. Der Export sagt überall LoadBearing=false, genau die Sorte Lücke, die die Raumbuch-Prüfung findet.'
          : 'No wall in this model is flagged load-bearing in Pset_WallCommon; the export says LoadBearing=false everywhere. That is exactly the kind of thing the room-book check catches.',
      );
    return done(
      de
        ? `${r.count} Wände sind tragend. Markiert.`
        : `${r.count} walls are load-bearing. Highlighted.`,
    );
  }

  if (type && COUNT.test(q)) {
    const perStorey =
      /per storey|per level|per floor|pro geschoss|pro stockwerk|je geschoss|pro etage/.test(q);
    const r = (await callTool(
      tools,
      'count_elements',
      { type, ...(storey ? { storey } : {}), group_by_storey: perStorey },
      calls,
    )) as { count: number; per_storey?: Record<string, number>; ids: number[] };
    const by = r.per_storey
      ? ' (' +
        Object.entries(r.per_storey)
          .map(([s, n]) => `${s}: ${n}`)
          .join(', ') +
        ')'
      : '';
    const noun = type.slice(3).toLowerCase();
    return done(
      de
        ? `${r.count} Elemente vom Typ ${noun}${storey ? ` in ${storey}` : ''}${by}. Markiert.`
        : `${r.count} ${noun}s${storey ? ` in ${storey}` : ''}${by}. Highlighted.`,
    );
  }

  if (type) {
    const r = (await callTool(
      tools,
      'find_elements',
      { type, ...(storey ? { storey } : {}) },
      calls,
    )) as { count: number };
    return done(
      de
        ? `${r.count} Elemente vom Typ ${type.slice(3).toLowerCase()} im Modell, markiert. Frag nach einer Anzahl pro Geschoss, einer Fläche, einem Material oder einer Eigenschaft.`
        : `${r.count} elements of type ${type.slice(3).toLowerCase()} in the model, highlighted. Ask for a count per storey, an area limit, a material or a property.`,
    );
  }

  return done(
    de
      ? 'Der Offline-Modus versteht Wände, Decken, Stützen, Träger, Türen, Fenster und Räume: Anzahl pro Geschoss, Flächen, Volumen nach Material (z. B. "wie viel Beton"), Brandschutz und tragend. Lade das Modell im Browser für freie Fragen.'
      : 'Offline mode understands walls, slabs, columns, beams, doors, windows and rooms: counts per storey, areas, volume by material ("how much concrete"), fire ratings and load-bearing. Load the browser model for free-form questions.',
    [],
  );
}

const AFFIRMATION = /^(yes|yeah|yep|sure|do it|go ahead|ja|jep|klar|mach das|ok|okay)[.!]?$/;

const STOREY_RE =
  /\badd (a |another )?(floor|storey|story)\b|put (another |a )?(floor|storey) on top|noch ein(en)? (geschoss|stockwerk)|ein weiteres geschoss|(geschoss|stockwerk) drauf/;
const ROOF_RE =
  /\badd (a |the |another |flat |gable )*roof\b|put a roof on|dach (drauf|hinzufügen|hinzufuegen)|ein dach (drauf|hinzufügen|hinzufuegen)|satteldach/;
const STAIR_RE =
  /\badd (a |an )?(stair|staircase|stairs|ramp)\b|treppe (hinzufügen|hinzufuegen|einbauen)|eine treppe/;
const WALL_RE = /\badd a wall\b|wand (hinzufügen|hinzufuegen)|füge eine wand|fuege eine wand/;
const OPENING_RE =
  /\b(cut|put|add)\b[^.]*\b(door|window)\b[^.]*\b(into|in)\b[^.]*\bwall\b|(tür|tuer|fenster)[^.]*\bin\b[^.]*\bwand\b/;
const OPENING_HOST = /(?:wall|wand)\s*#?\s*(\d{2,})/;
const MATERIAL_RE = /\bset\b[^.]*\bmaterial\b|\b(setze|setz)\b[^.]*\bmaterial\b/;
// "set material Concrete on #123" / "setze Material Beton auf #123"
const MATERIAL_NAME = /material\s+(?:"([^"]+)"|([^"]+?))\s+(?:on|auf|für|fuer)\b/;
// "x,y" is the coordinate separator here, not a decimal comma, so anchor on from/to rather
// than scanning for numbers blindly.
const WALL_COORDS =
  /(?:from|von)\s+(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s+(?:to|nach|bis)\s+(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/;
const UNDO_RE = /\b(undo|take (that|it) back|rückgängig|rueckgaengig)\b/;
const EXPORT_RE = /\b(export|download)\b.*(ifc|model|modell)|exportiere|speichere (das )?ifc/;
const REMOVE_STOREY_RE =
  /\b(remove|delete|take (off|away|down))\b[^.]*\b(top|upper|last|new) (floor|storey|story|level)\b|(oberste|letzte|neue)s? (geschoss|stockwerk|ebene)[^.]*(entfern|lösch|loesch|weg)/;
const REMOVE_IDS_RE = /\b(remove|delete|entferne|lösche|loesche)\b[^.]*#\d{2,}/;
// a change request in any wording: caught after the rules above so it never reads as a query
const CHANGE_RE =
  /\b(add|remove|delete|build|put|place|create|make|move|replace|change|set|extend|undo)\b|\b(füge|fuege|entferne|lösche|loesche|baue|setze|ersetze|verschiebe|ändere|aendere)\b/;

const BUILD_RES = [
  EXPORT_RE,
  UNDO_RE,
  STOREY_RE,
  ROOF_RE,
  STAIR_RE,
  OPENING_RE,
  MATERIAL_RE,
  WALL_RE,
  REMOVE_STOREY_RE,
  REMOVE_IDS_RE,
];

// The build intents, checked ahead of the type/material filters below so "add a wall" does
// not get read as a query about walls.
async function buildRoute(
  q: string,
  tools: Record<string, ToolFn>,
  calls: ToolCall[],
  de: boolean,
  ctx: AskContext,
): Promise<string | null> {
  if (EXPORT_RE.test(q)) return runBuild(tools, calls, 'export', {}, de);
  if (UNDO_RE.test(q)) return runBuild(tools, calls, 'undo', {}, de);
  if (REMOVE_STOREY_RE.test(q)) {
    const ids = await topStoreyIds(ctx);
    if (!ids.length)
      return de ? 'Kein Geschoss zum Entfernen gefunden.' : 'No storey found to remove.';
    return runBuild(tools, calls, 'remove', { ids }, de);
  }
  if (REMOVE_IDS_RE.test(q)) {
    const ids = [...q.matchAll(/#(\d{2,})/g)].map((m) => Number(m[1]));
    return runBuild(tools, calls, 'remove', { ids }, de);
  }
  if (STOREY_RE.test(q)) return runBuild(tools, calls, 'add_storey', {}, de);
  if (ROOF_RE.test(q)) {
    const kind = /\bflat\b|flachdach/.test(q)
      ? 'flat'
      : /gable|satteldach|giebel/.test(q)
        ? 'gable'
        : null;
    return runBuild(tools, calls, 'add_roof', kind ? { kind } : {}, de);
  }
  if (STAIR_RE.test(q)) return runBuild(tools, calls, 'add_stair', {}, de);
  if (OPENING_RE.test(q)) {
    const host = q.match(OPENING_HOST);
    const kind = /fenster|window/.test(q) ? 'window' : 'door';
    return runBuild(
      tools,
      calls,
      'add_opening',
      { kind, ...(host ? { host: Number(host[1]) } : {}) },
      de,
    );
  }
  if (MATERIAL_RE.test(q)) {
    const material = q.match(MATERIAL_NAME);
    const name = (material?.[1] ?? material?.[2] ?? '').trim();
    const ids = [...q.matchAll(/#(\d{2,})/g)].map((m) => Number(m[1]));
    if (!name || !ids.length)
      return de
        ? 'Nenne Material und Elemente: "setze Material Beton auf #1234".'
        : 'Name a material and the elements: "set material Concrete on #1234".';
    return runBuild(tools, calls, 'set_material', { material: name, ids }, de);
  }
  if (WALL_RE.test(q)) {
    const m = q.match(WALL_COORDS);
    if (!m)
      return de
        ? 'Nenne Start- und Endpunkt: "füge eine Wand von 0,0 bis 5,0 hinzu".'
        : 'Give a start and end point: "add a wall from 0,0 to 5,0".';
    const [x1, y1, x2, y2] = m.slice(1, 5).map(Number);
    return runBuild(tools, calls, 'add_wall', { start: [x1, 0, y1], end: [x2, 0, y2] }, de);
  }
  return null;
}

// The highest storey with anything on it, its contents first so the storey goes last.
async function topStoreyIds(ctx: AskContext): Promise<number[]> {
  const store = ctx.store;
  if (!store) return [];
  const storeys = [...(await store.storeys())].sort(
    (a, b) => (b.elevation ?? 0) - (a.elevation ?? 0),
  );
  const elements = await store.elements({ includeSpaces: true });
  for (const s of storeys) {
    const on = elements.filter((e) => e.storeyId === s.expressID).map((e) => e.expressID);
    if (on.length) return [...on, s.expressID];
  }
  return [];
}

async function runBuild(
  tools: Record<string, ToolFn>,
  calls: ToolCall[],
  action: string,
  params: Record<string, unknown>,
  de: boolean,
): Promise<string> {
  const r = (await callTool(tools, 'build', { action, ...params }, calls)) as Record<
    string,
    unknown
  >;
  if (r.error)
    return de ? `Das ging nicht: ${String(r.error)}` : `That did not work: ${String(r.error)}`;
  return buildText(r, de);
}

// A plain "yes" resolves every pending fix in turn, not just the first: add the stair, move
// the roof, and so on, until check_building has nothing actionable left (capped so a rule
// that never resolves cannot loop forever).
async function runSuggests(
  tools: Record<string, ToolFn>,
  calls: ToolCall[],
  first: Suggest,
  de: boolean,
): Promise<string> {
  const texts: string[] = [];
  let next: Suggest | undefined = first;
  for (let i = 0; i < 4 && next; i++) {
    const r = (await callTool(
      tools,
      'build',
      { action: next.action, ...(next.params ?? {}) },
      calls,
    )) as Record<string, unknown>;
    if (r.error) {
      texts.push(
        de ? `Das ging nicht: ${String(r.error)}` : `That did not work: ${String(r.error)}`,
      );
      break;
    }
    texts.push(buildText(r, de));
    next = suggestFrom(calls);
  }
  return texts.join(' ');
}

const CHANGED_RE =
  /what (has )?changed|since the original|was hat sich (ge)?(ä|ae)ndert|seit dem original/;
const SPREADSHEET = /\b(excel|xlsx|csv|spreadsheet|tabellenkalkulation)\b/;
const EXPORT_WORD = /\b(export|exportiere|download|herunterladen)/;
const QUANTITIES = /takeoff|quantities|mengen|massen/;
const VOLUME = /m³|m3\b|cubic|kubik|volume|volumen|rauminhalt/;
const AREA = /m²|m2\b|square met|quadratmet|\barea\b|fläche|flaeche/;
const HOW_MUCH = /how much|wie viel|wieviel|menge|amount|quantity|takeoff|massen/;
const COUNT =
  /how many|count|per storey|per level|per floor|wie viele|anzahl|pro geschoss|pro stockwerk|je geschoss|number of/;

const isType_ = (type: string | null, want: string) => type === want;

function storeyIn(q: string, elements: PropsElement[]): string | null {
  const names = new Set<string>();
  for (const e of elements) if (e.storey) names.add(e.storey);
  for (const n of names) {
    const l = n.toLowerCase();
    if (l.length >= 3 && q.includes(l)) return n;
  }
  const m = q.match(
    /\b(eg|og\s?\d|ug\s?\d?|level\s?\d|storey\s?\d|floor\s?\d|erdgeschoss|untergeschoss|dachgeschoss|ground floor|basement|roof)\b/,
  );
  if (!m) return null;
  const key = m[1]!.replace(/\s+/g, '');
  for (const n of names) if (n.toLowerCase().replace(/\s+/g, '').includes(key)) return n;
  return null;
}

export function takeoffText(r: TakeoffResult, de: boolean): string {
  const lang = de ? 'de' : 'en';
  const material = r.material ? (MATERIAL_CLASSES[r.material]?.label[lang] ?? r.material) : '';
  const type = r.type ? typeWords(r.type)[lang] : '';
  const what = de
    ? [material, type && (material ? `in ${type}` : type)].filter(Boolean).join(' ')
    : [material, type && (material ? `in ${type}` : type)].filter(Boolean).join(' ');
  const where = r.storey ? (de ? ` in ${r.storey}` : ` on ${r.storey}`) : '';
  const unit = r.unit === 'm3' ? 'm³' : r.unit === 'm2' ? 'm²' : '';
  if (r.elements === 0)
    return de
      ? `Kein Element passt auf ${what || 'diese Filter'}${where}.`
      : `Nothing in the model matches ${what || 'those filters'}${where}.`;
  const by = Object.entries(r.by_type)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([t, v]) => `${typeWords(t)[lang]} ${v.toFixed(1)}`)
    .join(', ');
  const src = Object.entries(r.sources)
    .map(([s, n]) => `${n} ${s}`)
    .join(', ');
  const parts: string[] = [];
  if (r.quantity === 'count') {
    parts.push(
      de ? `${r.total} Elemente${where} (${by}).` : `${r.total} elements${where} (${by}).`,
    );
  } else {
    const total = de ? r.total.toLocaleString('de-AT') : r.total.toLocaleString('en-GB');
    parts.push(
      de
        ? `${total} ${unit} ${what}${where} über ${r.elements} Elemente (${by}).`
        : `${total} ${unit} of ${what}${where} across ${r.elements} elements (${by}).`,
    );
    parts.push(de ? `Quellen: ${src}.` : `Sources: ${src}.`);
    if (r.layer_split > 0)
      parts.push(
        de
          ? `${r.layer_split} mehrschichtige Elemente wurden nach Schichtdicke aufgeteilt (Näherung).`
          : `${r.layer_split} layered elements were split by layer thickness (approximation).`,
      );
    if (r.mixed > 0)
      parts.push(
        de
          ? `${r.mixed} Elemente bestehen aus mehreren Materialien ohne Anteile und zählen ganz (Obergrenze).`
          : `${r.mixed} elements mix several materials without proportions and count in full (an upper bound).`,
      );
    if (r.missing > 0)
      parts.push(
        de
          ? `${r.missing} passende Elemente haben keine Menge und fehlen in der Summe.`
          : `${r.missing} matching elements carry no quantity and are not in the total.`,
      );
  }
  if (r.materials_matched.length)
    parts.push(
      (de ? 'Materialien: ' : 'Materials: ') + r.materials_matched.slice(0, 6).join(', ') + '.',
    );
  parts.push(de ? 'Markiert.' : 'Highlighted.');
  return parts.join(' ');
}
